// tabs/CounseleesTab.tsx
import { useCallback, useEffect, useState } from 'react';
import { Avatar, Badge, Card, Center, Group, SimpleGrid, Stack, Text, ThemeIcon, ActionIcon, Tooltip } from '@mantine/core';
import { IconUsers, IconRefresh } from '@tabler/icons-react';
import { MemberDetailModal } from '@/components';
import { useCareCaseService } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';

interface CounseleesTabProps {
  caseID: number;
  active: boolean;
}

// Shape from searchMembers response data[]
interface Member {
  memberID: number;
  userId: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  dateOfBirth: string | null;
  gender: string | null;
  maritalStatusID: number;
  email: string;
  cellNumber: string | null;
  altContact: string | null;
  telephone: string | null;
  isMember: boolean;
  isActive: boolean;
  /** Submitted the request, as opposed to a counselee who only needs care (SDK 0.0.58). */
  isRequester?: boolean;
  avatar: string | null;
  typeID: number;
  memberCode: string | null;
  modifiedDateTime: number;
  createdDateTime: number | null;
  // joined locally after enrichment
  isMain?: boolean;
  hasConsent?: boolean;
}

const fullName = (m: Member) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ').trim() || `Member ${m.memberID}`;

const getInitials = (m: Member) =>
  [m.firstName?.[0], m.lastName?.[0]].filter(Boolean).join('').toUpperCase() || 'M';

// ── Counselee grid card ──────────────────────────────────────────────────────
function CounseleeCard({
  member,
  onOpenDetails,
}: {
  member: Member;
  onOpenDetails: () => void;
}) {
  return (
    <Card
      withBorder
      radius="md"
      padding="lg"
      h="100%"
      onClick={onOpenDetails}
      style={{ cursor: 'pointer' }}
    >
      <Group justify="center" mb="sm">
        <Avatar src={member.avatar ?? undefined} alt={fullName(member)} size="xl" radius="md">
          {getInitials(member)}
        </Avatar>
      </Group>

      <Text fw={700} ta="center" size="md" mb={4} lineClamp={1}>
        {fullName(member)}
      </Text>

      {/* Who this person is on the case.
          ⚠️ `isRequester` and `isMain` are independent: a self-registration is both, an on-behalf
          requester is neither a counselee nor the main one. Badging them separately is the only way
          to tell "submitted the request" from "needs care". */}
      <Group justify="center" gap={6} mb={4}>
        {member.isRequester && (
          <Badge size="sm" variant="light" color="grape">Requester</Badge>
        )}
        {member.isMain && (
          <Badge size="sm" variant="light" color="blue">
            {member.isRequester ? 'Needs care' : 'Main Participant'}
          </Badge>
        )}
        {!member.isMain && !member.isRequester && (
          <Badge size="sm" variant="light" color="teal">Needs care</Badge>
        )}
        {/* <Badge size="sm" variant="light" color={member.hasConsent ? 'green' : 'red'}>
          {member.hasConsent ? 'Consent Given' : 'No Consent'}
        </Badge> */}
      </Group>

      <Stack align="center" gap={2}>
        {member.email
          ? <Text size="sm" c="dimmed">Email: {member.email}</Text>
          : <Text size="sm" c="dimmed">No email on record</Text>
        }
        {(member.cellNumber || member.telephone || member.altContact)
          ? <Text size="sm" c="dimmed">Phone: {member.cellNumber ?? member.telephone ?? member.altContact}</Text>
          : <Text size="sm" c="dimmed">No phone on record</Text>
        }
      </Stack>
    </Card>
  );
}

function CounseleeCardSkeleton() {
  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm">
        <Avatar radius="xl" size={52} color="gray">M</Avatar>
        <Stack gap={4}>
          <Text size="sm" c="dimmed">Loading…</Text>
          <Text size="xs" c="dimmed">—</Text>
          <Text size="xs" c="dimmed">—</Text>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CounseleesTab({ caseID, active }: CounseleesTabProps) {
  const careCaseService = useCareCaseService();

  const [counselees, setCounselees]   = useState<Member[]>([]);
  const [loadingCounselees, setLoadingCounselees] = useState(false);
  /** Card click opens the read-only detail modal. */
  const [detailMember, setDetailMember] = useState<Member | null>(null);

  /**
   * One call, not 1 + N.
   *
   * ⚠️ This used to call `members()` and then `memberService.searchMembers` **per counselee** to fill in
   * names and contacts. `getView(caseID).members` returns `CareCaseMemberView[]` with all of it already
   * — plus `isRequester` and `dateOfBirth`, which the bare `CareCaseMember` entity does not carry at
   * all. Do not go back to the entity endpoint for this list.
   */
  const fetchCounselees = useCallback(async () => {
    if (!caseID) return;
    setLoadingCounselees(true);
    try {
      const view = await careCaseService.getView(caseID);
      const rows = view?.members ?? [];

      setCounselees(rows.map((m) => {
        // ⚠️ The view gives one `fullName`; the card wants first/last for its initials. Splitting on
        // the last space is a display convenience only — nothing is written back from these.
        const full = (m.fullName ?? '').trim();
        const cut = full.lastIndexOf(' ');
        return {
          memberID: m.memberID,
          userId: '',
          firstName: cut > 0 ? full.slice(0, cut) : full || 'Member',
          middleName: null,
          lastName: cut > 0 ? full.slice(cut + 1) : '',
          dateOfBirth: m.dateOfBirth ?? null,
          gender: null,
          maritalStatusID: 0,
          email: m.email ?? '',
          cellNumber: m.phone ?? null,
          altContact: null,
          telephone: null,
          isMember: true,
          isActive: true,
          isRequester: m.isRequester,
          avatar: m.avatar ?? null,
          typeID: 0,
          memberCode: null,
          modifiedDateTime: 0,
          createdDateTime: null,
          isMain: m.isMain,
          hasConsent: m.hasConsent,
        } satisfies Member;
      }));
    } catch (e) {
      console.error('Error fetching counselees:', e);
      toast.error('Error', 'Could not load counselees.');
    } finally {
      setLoadingCounselees(false);
    }
  }, [caseID, careCaseService]);

  useEffect(() => { if (active) fetchCounselees(); }, [active, fetchCounselees]);

  return (
    <Stack gap="lg">
      {/* ── Header: title + refresh ── */}
      <Group justify="space-between" align="center">
        <Text fw={600} size="sm" c="dimmed">
          Counselees on this case ({counselees.length})
        </Text>
        <Group gap="xs">
          <Tooltip label="Refresh counselees" withArrow>
            <ActionIcon
              variant="subtle"
              onClick={fetchCounselees}
              loading={loadingCounselees}
              size="lg"
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* ── Counselees grid ── */}
      <Stack gap="xs">
        {loadingCounselees ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <CounseleeCardSkeleton />
            <CounseleeCardSkeleton />
            <CounseleeCardSkeleton />
          </SimpleGrid>
        ) : counselees.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={56} radius="xl" variant="light" color="teal">
                <IconUsers size={30} />
              </ThemeIcon>
              <Text fw={600}>No counselees yet</Text>
              <Text size="sm" c="dimmed" ta="center" maw={360}>
                No counselees have been added to this case yet.
              </Text>
            </Stack>
          </Center>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {counselees.map((c) => (
              <CounseleeCard
                key={c.memberID}
                member={c}
                onOpenDetails={() => setDetailMember(c)}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>

      {/* The card only has room for a name and one contact line; the rest lives here. */}
      <MemberDetailModal
        member={detailMember}
        opened={detailMember !== null}
        onClose={() => setDetailMember(null)}
        contextLabel="case"
      />
    </Stack>
  );
}